import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import jsonLogic from 'json-logic-js';
import { requiredMessage, predicateMessage, emailMessage, numberMessage } from '../validation/messages';
import { LanguageContext } from '../LanguageContext';
import { useNavigate } from 'react-router-dom';
import api from '../axiosInstance';
import { registry } from '../renderer/renderers';
import { coerceNumber, normalizeValue } from '../utils/normalization';
import { useWorkflowSchema, WORKFLOW_SCHEMA_STATUS } from '../hooks/useWorkflowSchema';

const CONDITIONAL_VISIBILITY_TYPES = new Set([
  'file-upload',
  'radio',
  'radios',
  'checkbox',
  'checkboxes',
  'input',
  'textarea',
  'character-count',
  'warning-text',
  'inset-text',
  'paragraph',
]);
const CONDITIONAL_OPTION_TYPES = new Set(['radio', 'radios', 'checkbox', 'checkboxes']);
const DEFAULTABLE_TYPES = new Set(['input', 'textarea', 'character-count']);
const DEFAULT_REF_REGEX = /^\{([-a-z0-9_.[\]]+)\}$/i;
const INPUT_TYPES = new Set(['input','text','email','phone','password','number','textarea','radio','checkbox','checkboxes','select','date','date-input','character-count','file-upload','signature-ack']);

function parseDefaultRef(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.trim().match(DEFAULT_REF_REGEX);
  return m ? m[1] : null;
}

function getByPath(obj, path) {
  if (obj == null || !path || typeof path !== 'string') return undefined;
  const tokens = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  let cur = obj;
  for (const t of tokens) {
    if (cur == null) return undefined;
    cur = cur[t];
  }
  return cur;
}

function coerceNumeric(a, b) {
  const na = typeof a === 'string' && a.trim() !== '' ? Number(a) : a;
  const nb = typeof b === 'string' && b.trim() !== '' ? Number(b) : b;
  if (Number.isFinite(na) && Number.isFinite(nb)) return [na, nb];
  return [a, b];
}

function looselyEqual(a, b) {
  if ((a === null && b === undefined) || (a === undefined && b === null)) return true;
  const [na, nb] = coerceNumeric(a, b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return a === b;
}

function extractDateInputParts(value) {
  if (value === undefined || value === null) return { empty: true };
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return { empty: true };
    const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return { empty: false, complete: false, malformed: true };
    return { empty: false, complete: true, year: match[1], month: match[2], day: match[3] };
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const day = String(value.day || value.d || '').trim();
    const month = String(value.month || value.m || '').trim();
    const year = String(value.year || value.y || '').trim();
    const empty = [day, month, year].every(part => part === '');
    if (empty) return { empty: true };
    return {
      empty: false,
      complete: Boolean(day && month && year && year.length === 4),
      day,
      month,
      year,
    };
  }
  return { empty: false, complete: false, malformed: true };
}

function daysInMonth(year, month) {
  if (month < 1 || month > 12) return 0;
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function isDatePartsAfterToday({ year, month, day }) {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;
  const todayDay = today.getDate();
  if (year !== todayYear) return year > todayYear;
  if (month !== todayMonth) return month > todayMonth;
  return day > todayDay;
}

function validateDateInputValue(value, { disallowFuture = false } = {}) {
  const parts = extractDateInputParts(value);
  if (parts.empty) return { ok: false, reason: 'empty' };
  if (!parts.complete || parts.malformed) return { ok: false, reason: 'incomplete' };
  if (!/^\d{1,2}$/.test(parts.day) || !/^\d{1,2}$/.test(parts.month) || !/^\d{4}$/.test(parts.year)) {
    return { ok: false, reason: 'invalid' };
  }
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return { ok: false, reason: 'invalid' };
  }
  if (month < 1 || month > 12) return { ok: false, reason: 'invalid' };
  const maxDay = daysInMonth(year, month);
  if (day < 1 || day > maxDay) return { ok: false, reason: 'invalid' };
  if (disallowFuture && isDatePartsAfterToday({ year, month, day })) {
    return { ok: false, reason: 'future' };
  }
  return {
    ok: true,
    iso: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
  };
}

function isDateOfBirthComponent(component, key) {
  const candidates = [
    key,
    component?.id,
    component?.storageKey,
    component?.name,
    component?.namePrefix,
    component?.label?.en,
    component?.label?.fr,
  ]
    .filter(Boolean)
    .map(value => String(value).trim().toLowerCase());
  return candidates.some(value =>
    value === 'dob' ||
    value === 'date-of-birth' ||
    value === 'date_of_birth' ||
    value === 'birthdate' ||
    value.includes('date of birth') ||
    value.includes('date de naissance')
  );
}

function toConditionTokenList(value) {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        return toConditionTokenList(parsed);
      } catch (_) {
        // Fall through to comma-splitting for simple author-entered strings.
      }
    }
    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
}

function hasToken(current, value) {
  const currentTokens = new Set(toConditionTokenList(current));
  const needles = toConditionTokenList(value);
  if (!needles.length) return false;
  return currentTokens.has(needles[0]);
}

function hasAnyToken(current, value) {
  const currentTokens = new Set(toConditionTokenList(current));
  const needles = toConditionTokenList(value);
  if (!needles.length) return false;
  return needles.some((needle) => currentTokens.has(needle));
}

function hasAllTokens(current, value) {
  const currentTokens = new Set(toConditionTokenList(current));
  const needles = toConditionTokenList(value);
  if (!needles.length) return false;
  return needles.every((needle) => currentTokens.has(needle));
}

function clone(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (_) {
    return obj;
  }
}

function resolveConditionalLookupTarget(lookup, condId) {
  if (!lookup || !condId) return null;
  const entry = lookup.get(condId);
  if (!entry) return null;
  if (entry && typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'component')) {
    return entry;
  }
  return { component: entry, index: null };
}

function cloneConditionalChild(component, suffix) {
  const cloneChild = clone(component);
  if (!cloneChild) return cloneChild;
  if (cloneChild.id) cloneChild.id = `${cloneChild.id}${suffix}`;
  if (cloneChild.props && cloneChild.props.id) {
    cloneChild.props = { ...cloneChild.props, id: `${cloneChild.props.id}${suffix}` };
  }
  return cloneChild;
}

function optionRevealChildren(opt) {
  if (Array.isArray(opt?.children)) return opt.children;
  if (opt?.conditional && Array.isArray(opt.conditional.children)) return opt.conditional.children;
  return [];
}

function inflateOptionChildren(component, lookup, { consumed = null, trail = new Set(), counterRef = { value: 0 } } = {}) {
  if (!component || typeof component !== 'object' || !lookup) return component;
  const nextComponent = { ...component };
  if (Array.isArray(component.children)) {
    nextComponent.children = component.children.map((child) =>
      inflateOptionChildren(child, lookup, { consumed, trail, counterRef })
    );
  }
  const type = String(component.type || component.template_key || '').toLowerCase();
  if (!CONDITIONAL_OPTION_TYPES.has(type)) return nextComponent;
  const key = Array.isArray(component.options) ? 'options' : (Array.isArray(component.items) ? 'items' : null);
  if (!key) return nextComponent;
  nextComponent[key] = component[key].map((opt) => {
    if (!opt || typeof opt !== 'object') return opt;
    const updated = { ...opt };
    let children = optionRevealChildren(opt).map((child) =>
      inflateOptionChildren(child, lookup, { consumed, trail, counterRef })
    );
    const condId = opt.conditionalChildId ? String(opt.conditionalChildId) : '';
    if (!children.length && condId && !trail.has(condId)) {
      const target = resolveConditionalLookupTarget(lookup, condId);
      if (target?.component && target.component !== component && (!consumed || !consumed.has(target.index))) {
        const suffix = `__cond${++counterRef.value}`;
        const nextTrail = new Set(trail);
        nextTrail.add(condId);
        const childClone = cloneConditionalChild(target.component, suffix);
        if (childClone) {
          children = [
            inflateOptionChildren(childClone, lookup, { consumed, trail: nextTrail, counterRef }),
          ];
          if (consumed && target.index != null) consumed.add(target.index);
          delete updated.conditionalChildId;
        }
      }
    }
    if (children.length) {
      updated.children = children;
      delete updated.conditionalChildId;
    }
    return updated;
  });
  return nextComponent;
}

function expandConditionalChildren(schema) {
  if (!Array.isArray(schema)) return schema;
  const cloned = clone(schema);
  return cloned.map((step) => {
    if (!Array.isArray(step?.components)) return step;
    const lookup = new Map();
    step.components.forEach((comp, idx) => {
      if (!comp) return;
      const keys = [];
      if (comp.name) keys.push(String(comp.name));
      if (comp.storageKey) keys.push(String(comp.storageKey));
      if (comp.id != null) keys.push(String(comp.id));
      if (comp.props?.name) keys.push(String(comp.props.name));
      if (comp.props?.id != null) keys.push(String(comp.props.id));
      keys.forEach((key) => {
        if (!lookup.has(key)) lookup.set(key, { component: comp, index: idx });
      });
    });

    const consumed = new Set();
    const counterRef = { value: 0 };
    step.components = step.components.map((comp) =>
      inflateOptionChildren(comp, lookup, { consumed, counterRef })
    );

    if (consumed.size) {
      step.components = step.components.filter((_, idx) => !consumed.has(idx));
    }
    return step;
  });
}

function extractJsonLogicVarRefs(node, refs) {
  if (!node) return;
  if (Array.isArray(node)) {
    node.forEach((entry) => extractJsonLogicVarRefs(entry, refs));
    return;
  }
  if (typeof node !== 'object') return;
  if (Object.prototype.hasOwnProperty.call(node, 'var')) {
    const raw = node.var;
    const candidate = Array.isArray(raw) ? raw[0] : raw;
    if (typeof candidate === 'string' && candidate.trim()) refs.add(candidate.trim());
  }
  Object.keys(node).forEach((key) => extractJsonLogicVarRefs(node[key], refs));
}

function extractValidationRefs(validation, refs) {
  if (!validation || typeof validation !== 'object') return;
  if (!Array.isArray(validation.rules)) return;
  validation.rules.forEach((rule) => {
    if (!rule || typeof rule !== 'object') return;
    const type = rule.type || rule.kind;
    if (type === 'compare') {
      if (typeof rule.left === 'string' && rule.left.trim()) refs.add(rule.left.trim());
      if (typeof rule.right === 'string' && rule.right.trim()) refs.add(rule.right.trim());
      return;
    }
    if (type === 'atLeastOne') {
      const fields = Array.isArray(rule.fields) ? rule.fields : (Array.isArray(rule.keys) ? rule.keys : []);
      fields.forEach((f) => {
        if (typeof f === 'string' && f.trim()) refs.add(f.trim());
      });
      return;
    }
    if (type === 'predicate' && rule.when) {
      extractJsonLogicVarRefs(rule.when, refs);
    }
  });
}

function normalizeServerHistory(candidate) {
  if (!Array.isArray(candidate)) return [];
  return candidate.filter((entry) => typeof entry === 'string' && entry.trim().length > 0);
}

function DynamicTestInner({ intakeFormSchema, allowSaveDraft, refreshSchema }) {
  // Capture target step early (before initial effects) so we know if user is resuming mid‑flow
  const initialTargetStepRef = useRef(null);
  if(initialTargetStepRef.current===null && typeof window!=='undefined'){
    try { const p=new URLSearchParams(window.location.search||''); const t=p.get('step'); if(t) initialTargetStepRef.current=t; } catch(_) {}
  }
  const normalizedSchema = useMemo(() => expandConditionalChildren(intakeFormSchema), [intakeFormSchema]);

  const computedInitialIndex = (()=>{ if(initialTargetStepRef.current){ const idx=normalizedSchema.findIndex(s=>s.stepId===initialTargetStepRef.current); if(idx>=0) return idx; } return 0; })();
  const [stepIndex, setStepIndex] = useState(computedInitialIndex);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [navigationError, setNavigationError] = useState('');
  const [stepData, setStepData] = useState({});
  const [visibleHydration, setVisibleHydration] = useState({});
  const [applicableStepIds, setApplicableStepIds] = useState([]);
  const [touchedKeys, setTouchedKeys] = useState(() => new Set());
  // Removed preview modal in favor of direct submission
  const formRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const errorsRef = useRef(errors);
  const warningsRef = useRef(warnings);
  const computeValidationRef = useRef(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const [returnToSummary, setReturnToSummary] = useState(false);
  const focusErrorSummaryNext = useRef(false);
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const step = normalizedSchema[stepIndex];
  const isMounted = useRef(true);
  const totalSchemaSteps = Array.isArray(normalizedSchema) ? normalizedSchema.length : 0;


  const storage = step?.storage || {};
  const defaultGet = '/api/intake-json';
  const defaultSave = '/api/intake-json';
  const getUrl = storage.getEndpoint || (step?.stepId ? `/api/intake-step/${step.stepId}` : defaultGet);
  const saveUrl = storage.saveEndpoint || (step?.stepId ? `/api/intake-step/${step.stepId}/update` : defaultSave);
  const isAggregate = saveUrl === '/api/intake-json';

  const setByPath = (obj, path, value) => {
    if (!path || typeof path !== 'string') return;
    const tokens = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
    let cur = obj;
    for (let i = 0; i < tokens.length; i++) {
      const k = tokens[i];
      const last = i === tokens.length - 1;
      if (last) cur[k] = value; else {
        if (typeof cur[k] !== 'object' || cur[k] == null) {
          const nextIsIndex = /^\d+$/.test(tokens[i + 1]);
            cur[k] = nextIsIndex ? [] : {};
        }
        cur = cur[k];
      }
    }
  };

  const lookupDefaultRef = (ref, primary, secondary) => {
    if (!ref) return undefined;
    const fromPrimary = primary
      ? (Object.prototype.hasOwnProperty.call(primary, ref) ? primary[ref] : getByPath(primary, ref))
      : undefined;
    if (fromPrimary !== undefined) return fromPrimary;
    return secondary
      ? (Object.prototype.hasOwnProperty.call(secondary, ref) ? secondary[ref] : getByPath(secondary, ref))
      : undefined;
  };

  const resolveDefaultValue = (component, primaryAnswers, secondaryAnswers) => {
    const type = String(component?.type || component?.template_key || '').toLowerCase();
    if (!DEFAULTABLE_TYPES.has(type)) return undefined;
    const rawDefault = component?.value ?? component?.defaultValue ?? component?.props?.value ?? component?.props?.defaultValue;
    if (rawDefault === undefined || rawDefault === null) return undefined;
    const extractString = (val) => {
      if (typeof val === 'string') return val;
      if (val && typeof val === 'object') {
        if (typeof val[language] === 'string') return val[language];
        if (typeof val.en === 'string') return val.en;
        if (typeof val.fr === 'string') return val.fr;
      }
      return null;
    };
    const defaultString = extractString(rawDefault);
    const ref = parseDefaultRef(defaultString);
    if (ref) {
      const resolved = lookupDefaultRef(ref, primaryAnswers, secondaryAnswers);
      if (resolved !== undefined) return resolved;
      // If ref missing, fall back to static default (string/object)
    }
    return defaultString !== null ? defaultString : rawDefault;
  };

  const keyFor = useCallback((c) => {
    if (!c || typeof c !== 'object') return undefined;
    return c.storageKey || c.name || c?.props?.name || c.id;
  }, []);
  const getVal = (c, data = stepData) => {
    const k = keyFor(c); if(!k) return '';
    const type = String(c?.type || '').toLowerCase();
    let current;
    if(!isAggregate) {
      current = data && Object.prototype.hasOwnProperty.call(data, k) ? data[k] : undefined;
      if (current === undefined) current = getByPath(data, k);
    } else {
      try {
        current = getByPath(data, k);
        if (current === undefined && data && Object.prototype.hasOwnProperty.call(data, k)) current = data[k];
      } catch { current = undefined; }
    }
    const hasUserValue = (!valueIsEmpty(current, type)) || touchedKeys.has(k);
    if (hasUserValue) return current ?? '';
    const fallback = resolveDefaultValue(c, data, visibleHydration);
    if (fallback !== undefined) return fallback;
    return current ?? '';
  };
  const setVal = (c, value) => {
    const k = keyFor(c);
    setStepData(prev => {
      if(!k) return prev;
      if(isAggregate) {
        const next = Array.isArray(prev) ? [...prev] : { ...prev };
        try { setByPath(next, k, value); } catch { next[k] = value; }
        return next;
      }
      return { ...prev, [k]: value };
    });
    if (k) {
      setTouchedKeys(prev => {
        if (prev.has(k)) return prev;
        const next = new Set(prev);
        next.add(k);
        return next;
      });
    }
  };

  // ---------------- File-upload conditional visibility (AND semantics) ----------------
  const __componentRefMap = useMemo(() => {
    const map = new Map();
    try {
      normalizedSchema.forEach(s => {
        (s.components||[]).forEach(function visit(c){
          if(!c||typeof c!=='object') return;
          const k = keyFor(c);
          if(k) map.set(k, c);
          if(c.id && !map.has(c.id)) map.set(c.id, c);
          if(c.name && !map.has(c.name)) map.set(c.name, c);
          if(Array.isArray(c.children)) c.children.forEach(visit);
          if(Array.isArray(c.options)) c.options.forEach(opt=>{
            if(Array.isArray(opt.children)) opt.children.forEach(visit);
            if(opt.conditional && Array.isArray(opt.conditional.children)) opt.conditional.children.forEach(visit);
          });
        });
      });
    } catch {}
    return map;
  }, [keyFor, normalizedSchema]);
  const refValue = useCallback((ref, answers) => {
    if(ref==null) return undefined;
    if(Object.prototype.hasOwnProperty.call(answers, ref)) return answers[ref];
    const byPath = getByPath(answers, ref);
    if (byPath !== undefined) return byPath;
    const comp = __componentRefMap.get(ref);
    if(comp){
      const k = keyFor(comp);
      if(k && Object.prototype.hasOwnProperty.call(answers, k)) return answers[k];
      if(comp.id && Object.prototype.hasOwnProperty.call(answers, comp.id)) return answers[comp.id];
      if(comp.name && Object.prototype.hasOwnProperty.call(answers, comp.name)) return answers[comp.name];
    }
    return undefined;
  }, [__componentRefMap, keyFor]);
  const evaluateFileUploadConditions = useCallback((component, answers) => {
    if(!component) return true;
    const conds = component.conditions || component.props?.conditions;
    if(!conds || !conds.all) return true;
    const rules = Array.isArray(conds.all)? conds.all : [];
    for(const r of rules){ if(!r||typeof r!=='object') return true; const { ref, op, value } = r; const current = refValue(ref, answers); switch(op){ case 'exists': if(current===undefined||current===null||(typeof current==='string'&&current.trim()==='')) return false; break; case 'notExists': if(!(current===undefined||current===null||(typeof current==='string'&&current.trim()===''))) return false; break; case 'emptyOrZero': { if(current===undefined||current===null) break; if(typeof current==='string'){ const trimmed=current.trim(); if(trimmed==='') break; const num=Number(trimmed); if(Number.isFinite(num) && num===0) break; return false; } if(typeof current==='number'){ if(Number.isFinite(current) && current===0) break; return false; } if(Array.isArray(current)){ if(current.length===0) break; return false; } if(current===false) break; return false; } case 'equals': if(!looselyEqual(current, value)) return false; break; case 'notEquals': if(looselyEqual(current, value)) return false; break; case 'contains': if(!hasToken(current, value)) return false; break; case 'notContains': if(hasToken(current, value)) return false; break; case 'containsAny': if(!hasAnyToken(current, value)) return false; break; case 'notContainsAny': if(hasAnyToken(current, value)) return false; break; case 'containsAll': if(!hasAllTokens(current, value)) return false; break; case '>': { const [a,b]=coerceNumeric(current,value); if(!(Number.isFinite(a)&&Number.isFinite(b)) || !(a>b)) return false; break;} case '<': { const [a,b]=coerceNumeric(current,value); if(!(Number.isFinite(a)&&Number.isFinite(b)) || !(a<b)) return false; break;} default: break; } }
    return true;
  }, [refValue]);
  const mergedAnswersForConditions = useMemo(()=>({ ...visibleHydration, ...stepData }), [stepData, visibleHydration]);
  const hiddenConditionalKeys = useMemo(()=>{
    const hidden=new Set(); if(!step||!Array.isArray(step.components)) return hidden; const visit=(list)=>{ (list||[]).forEach(c=>{ if(!c||typeof c!=='object') return; const type=String(c.type||'').toLowerCase(); if(CONDITIONAL_VISIBILITY_TYPES.has(type)){ const k=keyFor(c); const hasConds = (c.conditions && c.conditions.all) || (c.props && c.props.conditions && c.props.conditions.all);
        if(k && hasConds){
          const visible=evaluateFileUploadConditions(c, mergedAnswersForConditions);
          if(!visible) hidden.add(k);
        }
      } if(Array.isArray(c.children)) visit(c.children); if(Array.isArray(c.options)) c.options.forEach(opt=>{ if(Array.isArray(opt.children)) visit(opt.children); if(opt.conditional && Array.isArray(opt.conditional.children)) visit(opt.conditional.children); }); }); }; visit(step.components); return hidden; }, [evaluateFileUploadConditions, keyFor, step, mergedAnswersForConditions]);

  // Gather all file-upload keys in current step (including nested) to detect if they are all hidden
  const allFileUploadKeysInStep = useMemo(()=>{
    const keys=[]; if(!step||!Array.isArray(step.components)) return keys; const visit=(list)=>{ (list||[]).forEach(c=>{ if(!c||typeof c!=='object') return; const type=String(c.type||'').toLowerCase(); if(type==='file-upload'){ const k=keyFor(c); if(k) keys.push(k); } if(Array.isArray(c.children)) visit(c.children); if(Array.isArray(c.options)) c.options.forEach(opt=>{ if(Array.isArray(opt.children)) visit(opt.children); if(opt.conditional && Array.isArray(opt.conditional.children)) visit(opt.conditional.children); }); }); }; visit(step.components); return keys; }, [keyFor, step]);
  const allUploadsHidden = useMemo(()=> allFileUploadKeysInStep.length>0 && allFileUploadKeysInStep.every(k=> hiddenConditionalKeys.has(k)), [allFileUploadKeysInStep, hiddenConditionalKeys]);
  useEffect(()=>{ if(!hiddenConditionalKeys.size) return; setStepData(prev=>{ let changed=false; const next={...prev}; hiddenConditionalKeys.forEach(k=>{ if(Object.prototype.hasOwnProperty.call(next,k)){ delete next[k]; changed=true; } }); return changed? next:prev; }); setVisibleHydration(prev=>{ let changed=false; const next={...prev}; hiddenConditionalKeys.forEach(k=>{ if(Object.prototype.hasOwnProperty.call(next,k)){ delete next[k]; changed=true; } }); return changed? next:prev; }); setErrors(prev=>{ if(!prev) return prev; const next={...prev}; let changed=false; hiddenConditionalKeys.forEach(k=>{ if(next[k]){ delete next[k]; changed=true; } }); return changed? next:prev; }); setWarnings(prev=>{ if(!prev) return prev; const next={...prev}; let changed=false; hiddenConditionalKeys.forEach(k=>{ if(next[k]){ delete next[k]; changed=true; } }); return changed? next:prev; }); }, [hiddenConditionalKeys]);

  function mergedLogicData(){
    const data = { ...visibleHydration, ...stepData };
    (step?.components || []).forEach(c => {
      const k = keyFor(c);
      if (k && c.id && data[k] !== undefined && data[c.id] === undefined) data[c.id] = data[k];
      if (k && c.storageKey && data[c.id] !== undefined && data[k] === undefined) data[k] = data[c.id];
    });
    return data;
  }

  const evaluateCondition = useCallback((condition, data) => {
    if(!condition) return true;
    try { return !!jsonLogic.apply(condition, data); } catch { return false; }
  }, []);

  const visibleStepIndices = useMemo(() => {
    const allowed = new Set(applicableStepIds);
    const indices = normalizedSchema.reduce((acc, candidateStep, idx) => {
      if (allowed.has(candidateStep?.stepId)) acc.push(idx);
      return acc;
    }, []);
    return indices.length ? indices : normalizedSchema.map((_, idx) => idx);
  }, [applicableStepIds, normalizedSchema]);
  const totalSteps = visibleStepIndices.length || totalSchemaSteps;
  const currentVisibleStepIndex = visibleStepIndices.indexOf(stepIndex);
  const currentStepNumber =
    currentVisibleStepIndex >= 0
      ? currentVisibleStepIndex + 1
      : Math.min(stepIndex + 1, totalSteps || 1);
  const hasVisibleNextStep = useMemo(
    () => visibleStepIndices.some(index => index > stepIndex),
    [stepIndex, visibleStepIndices]
  );
  const progressPercent = totalSteps > 1 ? Math.round((currentStepNumber - 1) * 100 / (totalSteps - 1)) : 0;

  const flattenComponents = useCallback(() => {
    const out=[]; const seen=new Set();
    function visit(list) {
      if(!Array.isArray(list)) return;
      list.forEach(c => {
        if(!c||typeof c!=='object') return;
        const k=keyFor(c);
        if(k && !seen.has(k)) { seen.add(k); out.push(c); }
        else if(!k) out.push(c);
        if(Array.isArray(c.options)) c.options.forEach(opt=>{ if(Array.isArray(opt.children)) visit(opt.children); if(opt.conditional && Array.isArray(opt.conditional.children)) visit(opt.conditional.children); });
        if(Array.isArray(c.children)) visit(c.children);
      });
    }
    visit(step?.components||[]);
    return out;
  }, [keyFor, step?.components]);

  const isPersistable = useCallback((c)=> {
    if(!c||typeof c!=='object') return false;
    const k = keyFor(c);
    return !!k && INPUT_TYPES.has(String(c.type||'').toLowerCase());
  }, [keyFor]);
  const stepHydrationKeys = useMemo(() => {
    const refs = new Set();
    const addRef = (raw) => {
      if (typeof raw !== 'string') return;
      const cleaned = raw.trim();
      if (!cleaned) return;
      refs.add(cleaned);
    };
    const readLocalizedString = (val) => {
      if (typeof val === 'string') return val;
      if (val && typeof val === 'object') {
        if (typeof val[language] === 'string') return val[language];
        if (typeof val.en === 'string') return val.en;
        if (typeof val.fr === 'string') return val.fr;
      }
      return null;
    };
    const visit = (component) => {
      if (!component || typeof component !== 'object') return;
      addRef(keyFor(component));
      extractJsonLogicVarRefs(component.showIf, refs);
      extractJsonLogicVarRefs(component.hideIf, refs);
      const conds = component.conditions || component.props?.conditions;
      if (conds && Array.isArray(conds.all)) {
        conds.all.forEach((rule) => {
          if (rule && typeof rule.ref === 'string') addRef(rule.ref);
        });
      }
      if (String(component.type || component.template_key || '').toLowerCase() && DEFAULTABLE_TYPES.has(String(component.type || component.template_key || '').toLowerCase())) {
        const rawDefault = component?.value ?? component?.defaultValue ?? component?.props?.value ?? component?.props?.defaultValue;
        const defaultString = readLocalizedString(rawDefault);
        const ref = parseDefaultRef(defaultString);
        if (ref) addRef(ref);
      }
      extractValidationRefs(component.validation || component.props?.validation, refs);
      if (String(component.type || '').toLowerCase() === 'summary-list' && Array.isArray(component.rows)) {
        component.rows.forEach((row) => {
          if (!row || typeof row !== 'object') return;
          addRef(row.key);
          addRef(row.originalKey);
        });
      }
      if (Array.isArray(component.children)) component.children.forEach(visit);
      if (Array.isArray(component.options)) {
        component.options.forEach((opt) => {
          if (!opt || typeof opt !== 'object') return;
          if (Array.isArray(opt.children)) opt.children.forEach(visit);
          if (opt.conditional && Array.isArray(opt.conditional.children)) opt.conditional.children.forEach(visit);
        });
      }
    };
    (step?.components || []).forEach(visit);
    return refs;
  }, [keyFor, language, step]);

  const fetchStepEnvelope = useCallback(async (targetStepId, targetUrl = null) => {
    const url = targetUrl || `/api/intake-step/${encodeURIComponent(targetStepId || '')}`;
    const response = await api.get(url);
    const raw = response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
      ? response.data
      : {};
    const hasEnvelope = raw.values && typeof raw.values === 'object' && !Array.isArray(raw.values);
    return {
      values: hasEnvelope ? raw.values : raw,
      history: normalizeServerHistory(raw.history),
      stepCursor: raw.stepCursor || null,
      restoredFromDraft: Boolean(raw.restoredFromDraft),
      applicableStepIds: Array.isArray(raw.navigation?.applicableStepIds)
        ? raw.navigation.applicableStepIds.filter(item => typeof item === 'string' && item.trim())
        : normalizedSchema.map(item => item?.stepId).filter(Boolean),
    };
  }, [normalizedSchema]);

  const readCanonicalHistory = useCallback(async () => {
    try {
      const res = await api.get('/api/intake-json');
      return normalizeServerHistory(res?.data?.history);
    } catch (_) {
      return [];
    }
  }, []);

  const persistRecoveryDraft = useCallback(async ({ stepCursor, draftPatch }) => {
    if (!allowSaveDraft || !stepCursor) return;
    try {
      await api.post('/api/draft/autosave', {
        stepCursor,
        dataPatch: draftPatch && typeof draftPatch === 'object' ? draftPatch : {},
      });
    } catch (_) {}
  }, [allowSaveDraft]);

  const buildVisibleHydration = useCallback((aggregate) => {
    const out = {};
    if (!aggregate || typeof aggregate !== 'object' || Array.isArray(aggregate)) return out;
    stepHydrationKeys.forEach((ref) => {
      const hasDirect = Object.prototype.hasOwnProperty.call(aggregate, ref);
      const resolved = hasDirect ? aggregate[ref] : getByPath(aggregate, ref);
      if (resolved === undefined) return;
      out[ref] = resolved;
      if (ref.includes('.') || ref.includes('[')) {
        try { setByPath(out, ref, resolved); } catch (_) {}
      }
    });
    return out;
  }, [stepHydrationKeys]);

  useEffect(() => {
    setTouchedKeys(new Set());
    setVisibleHydration({});
  }, [stepIndex]);

  useEffect(() => {
    let cancelled = false;
    isMounted.current = true;
    setErrors({});
    setWarnings({});
    setLiveAnnouncement('');
    focusErrorSummaryNext.current=false;
    setLoading(true);
    setError('');
    setStepData({});
    setVisibleHydration({});
    (async () => {
      try {
        const envelope = await fetchStepEnvelope(step?.stepId, getUrl);
        if (!isMounted.current || cancelled) return;
        const restoredStepCursor = envelope.stepCursor || null;
        if (!initialTargetStepRef.current && envelope.restoredFromDraft && restoredStepCursor) {
          const restoredIdx = normalizedSchema.findIndex((candidateStep) => candidateStep.stepId === restoredStepCursor);
          if (restoredIdx >= 0 && restoredIdx !== stepIndex) {
            initialTargetStepRef.current = restoredStepCursor;
            setStepIndex(restoredIdx);
            return;
          }
        }
        const hydration = buildVisibleHydration(envelope.values);
        setApplicableStepIds(envelope.applicableStepIds);
        if (step?.stepId && !envelope.applicableStepIds.includes(step.stepId)) {
          const nextApplicableId = envelope.applicableStepIds.find(id => {
            const index = normalizedSchema.findIndex(candidate => candidate.stepId === id);
            return index > stepIndex;
          }) || envelope.applicableStepIds[envelope.applicableStepIds.length - 1] || null;
          const nextApplicableIndex = normalizedSchema.findIndex(candidate => candidate.stepId === nextApplicableId);
          if (nextApplicableIndex >= 0 && nextApplicableIndex !== stepIndex) {
            setStepIndex(nextApplicableIndex);
            return;
          }
        }
        const comps = flattenComponents();
        const editableKeys = comps.filter(isPersistable).map(c => keyFor(c)).filter(Boolean);
        const hydratedPayload = {};
        editableKeys.forEach((k) => {
          const fromHydration = Object.prototype.hasOwnProperty.call(hydration, k) ? hydration[k] : getByPath(hydration, k);
          if (fromHydration === undefined) return;
          hydratedPayload[k] = fromHydration;
          if (k.includes('.') || k.includes('[')) {
            try { setByPath(hydratedPayload, k, fromHydration); } catch (_) {}
          }
        });
        setVisibleHydration(hydration);
        setStepData(hydratedPayload);
        const nextTouched = new Set();
        editableKeys.forEach((key) => {
          const val = isAggregate ? getByPath(hydratedPayload, key) : hydratedPayload[key];
          const hasValue =
            val !== undefined &&
            val !== null &&
            !(typeof val === 'string' && val.trim() === '') &&
            !(Array.isArray(val) && val.length === 0);
          if (hasValue) nextTouched.add(key);
        });
        setTouchedKeys(nextTouched);
        if (step?.stepId) {
          const serverHistory = envelope.history;
          if (serverHistory[serverHistory.length - 1] !== step.stepId) {
            await api.post('/api/intake-json', { history: [...serverHistory, step.stepId] });
          }
        }
      } catch (_) {
        if (isMounted.current && !cancelled) {
          setError(language === 'fr' ? 'Impossible de charger cette étape.' : 'Unable to load this step.');
        }
      } finally {
        if (isMounted.current && !cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      isMounted.current=false;
    };
  }, [buildVisibleHydration, fetchStepEnvelope, flattenComponents, getUrl, isAggregate, isPersistable, keyFor, language, normalizedSchema, step, step?.stepId, stepIndex]);

  const msgFor = (m) => { if(!m) return ''; if(typeof m==='string') return m; if(typeof m==='object') return m[language] || m.en || Object.values(m)[0] || ''; return String(m); };

  function migrateValidation(raw){
    if(!raw||typeof raw!=='object') return { required:false, rules:[] };
    const v = JSON.parse(JSON.stringify(raw));
    if(!v.requiredMessage && v.errorMessage){ v.requiredMessage = typeof v.errorMessage==='object'? v.errorMessage : { en:v.errorMessage, fr:v.errorMessage }; }
    if(v.pattern){ const exists = Array.isArray(v.rules)&&v.rules.some(r=>(r.type||r.kind)==='pattern'); if(!exists) v.rules=[...(v.rules||[]), { id:'auto-pattern', type:'pattern', trigger:['submit'], pattern:v.pattern }]; delete v.pattern; }
    if(v.minLength){ const exists = Array.isArray(v.rules)&&v.rules.some(r=>(r.type||r.kind)==='length'); if(!exists) v.rules=[...(v.rules||[]), { id:'auto-length', type:'length', trigger:['submit'], minLength:v.minLength }]; delete v.minLength; }
    if(Array.isArray(v.rules)) v.rules = v.rules.map(r=>{ if(!r||typeof r!=='object') return r; const out={...r}; if(!out.type && out.kind) out.type=out.kind; if(out.type==='atLeastOne' && Array.isArray(out.keys) && !out.fields) out.fields=out.keys; if(!Array.isArray(out.trigger)||!out.trigger.length) out.trigger=['submit']; if(!out.severity) out.severity='error'; if(out.block===undefined) out.block= out.severity==='error'; return out; }); else v.rules=[]; return v;
  }
  const FILE_UPLOAD_VALUE_KEYS = ['filePath', 'file_path', 'path', 'key', 'fileId', 'file_id', 'name', 'originalFilename', 'original_filename'];
  function hasFileUploadValue(val) {
    if (val === undefined || val === null) return false;
    if (typeof val === 'string') return false;
    if (typeof val === 'number') return Number.isFinite(val);
    if (Array.isArray(val)) return val.some(hasFileUploadValue);
    if (typeof val === 'object') {
      return FILE_UPLOAD_VALUE_KEYS.some((k) => {
        const v = val[k];
        if (v === undefined || v === null) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (typeof v === 'number') return Number.isFinite(v);
        return false;
      });
    }
    return false;
  }
  function valueIsEmpty(val, compType){ if((compType==='checkbox'||compType==='checkboxes') && val===false) return true; if(compType==='file-upload') return !hasFileUploadValue(val); if(val===undefined||val===null) return true; if(typeof val==='string') return val.trim()===''; if(Array.isArray(val)) return val.length===0; if(compType==='date'||compType==='date-input'){ if(val&&typeof val==='object'){ const d={ day:val.day||val.d||'', month:val.month||val.m||'', year:val.year||val.y||'' }; return [d.day,d.month,d.year].every(x=> String(x||'').trim()===''); } } return false; }
  function evaluateRule(rule, comp, value, data){ const type=rule.type||rule.kind; const msg=(lang)=> msgFor(rule.message)||null; const failResult=(reason)=>({ failed:true, message: msg(language)||reason }); try{ switch(type){ case 'predicate': if(!rule.when) return { failed:false }; return !!jsonLogic.apply(rule.when,data)? failResult(predicateMessage(language)) : { failed:false }; case 'atLeastOne': { const fields=Array.isArray(rule.fields)?rule.fields:[]; const ok=fields.some(f=>{ const v=data[f]; if(v==null) return false; if(Array.isArray(v)) return v.length>0; if(typeof v==='object') return Object.keys(v).length>0; return String(v).trim()!==''; }); return ok? { failed:false }: failResult(language==='fr'?'Fournissez au moins une valeur.':'Provide at least one value.'); } case 'range': { if(valueIsEmpty(value, comp.type)) return { failed:false }; const num=coerceNumber(value); if(!Number.isFinite(num)) return { failed:false }; if(rule.min!=null && num<rule.min) return failResult(language==='fr'?`La valeur doit être ≥ ${rule.min}.`:`Value must be ≥ ${rule.min}.`); if(rule.max!=null && num>rule.max) return failResult(language==='fr'?`La valeur doit être ≤ ${rule.max}.`:`Value must be ≤ ${rule.max}.`); return { failed:false }; } case 'length': { if(typeof value!=='string'||value==='') return { failed:false }; if(rule.minLength!=null && value.length<rule.minLength) return failResult(language==='fr'?`Minimum ${rule.minLength} caractères.`:`Minimum ${rule.minLength} characters.`); if(rule.maxLength!=null && value.length>rule.maxLength) return failResult(language==='fr'?`Maximum ${rule.maxLength} caractères.`:`Maximum ${rule.maxLength} characters.`); return { failed:false }; } case 'pattern': { if(typeof value!=='string'||value==='') return { failed:false }; if(!rule.pattern) return { failed:false }; try{ const re=new RegExp(rule.pattern, rule.flags||''); if(!re.test(value)) return failResult(language==='fr'?'Format invalide.':'Invalid format.'); }catch(_){ return { failed:false }; } return { failed:false }; } case 'compare': { const resolve=(o)=> (typeof o==='string' && Object.prototype.hasOwnProperty.call(data,o))? data[o]: o; const left=resolve(rule.left); const right=resolve(rule.right); const leftNum=coerceNumber(left); const rightNum=coerceNumber(right); let result=true; switch(rule.op){ case '==': result=looselyEqual(left,right); break; case '!=': result=!looselyEqual(left,right); break; case '>': result=leftNum>rightNum; break; case '>=': result=leftNum>=rightNum; break; case '<': result=leftNum<rightNum; break; case '<=': result=leftNum<=rightNum; break; default: return { failed:false }; } if(!result) return failResult(language==='fr'? 'Les valeurs ne correspondent pas.' : 'Values do not match.'); return { failed:false }; } default: return { failed:false }; } } catch(_){ return { failed:false }; } }
  function evaluateChangeRules(component, nextValue){ focusErrorSummaryNext.current=false; const migrated=migrateValidation(component?.validation); const rules=migrated.rules; if(!Array.isArray(rules)||!rules.length) return; const k=keyFor(component); if(!k) return; const data={ ...mergedLogicData(), [k]: nextValue }; let firstError=null; let warningMsg=null; for(const r of rules){ const triggers=Array.isArray(r.trigger)?r.trigger:['submit']; if(!triggers.includes('change')) continue; const { failed, message } = evaluateRule(r, component, nextValue, data); if(failed){ if((r.severity||'error')==='warn'){ warningMsg=message; continue; } firstError=message; break; } } setWarnings(prev=>{ const n={...prev}; if(warningMsg) n[k]=warningMsg; else delete n[k]; return n; }); setErrors(prev=>{ const hadPrev=!!prev[k]; const n={...prev}; if(firstError) n[k]=firstError; else delete n[k]; if(firstError && firstError!==prev[k]) setLiveAnnouncement(`${component.label ? (typeof component.label==='object' ? (component.label[language]||component.label.en||Object.values(component.label)[0]) : component.label)+': ' : ''}${firstError}`); else if(!firstError && hadPrev) setLiveAnnouncement(''); return n; }); }
  function handleFieldChange(component, value){ setError(''); const k=keyFor(component); setVal(component,value); setTimeout(()=>evaluateChangeRules(component,value),0); if(k && errors[k]){ setErrors(prev=>{ const n={...prev}; if(n[k] && !(component?.validation?.rules||[]).some(r=>(Array.isArray(r.trigger)?r.trigger:['submit']).includes('change'))) delete n[k]; return n; }); } }
  function anchorIdFor(c){ const k=keyFor(c); const type=String(c.type||'').toLowerCase(); if(type==='radio' || type==='checkbox' || type==='checkboxes') return `${k}-0`; if(type==='date' || type==='date-input') return `${k}-day`; return k; }
  useEffect(()=>{ const hasErrors=Object.keys(errors||{}).length>0; if(hasErrors && focusErrorSummaryNext.current && errorSummaryRef.current){ const prefix=language==='fr'? 'Erreur: ' : 'Error: '; if(!document.title.startsWith(prefix)) document.title = `${prefix}${document.title.replace(/^Erreur: |^Error: /,'')}`; errorSummaryRef.current.focus(); focusErrorSummaryNext.current=false; } }, [errors, language]);

  useEffect(() => {
    errorsRef.current = errors;
    warningsRef.current = warnings;
  }, [errors, warnings]);
  computeValidationRef.current = computeValidation;

  useEffect(() => {
    const errorKeys = Object.keys(errorsRef.current || {});
    const warningKeys = Object.keys(warningsRef.current || {});
    if (!errorKeys.length && !warningKeys.length) return;
    const compute = computeValidationRef.current;
    if (typeof compute !== 'function') return;
    const keys = new Set([...errorKeys, ...warningKeys]);
    const { stepErrors, stepWarnings } = compute({ limitKeys: keys, ruleTriggers: ['submit', 'change'] });
    setErrors(stepErrors);
    setWarnings(stepWarnings);
  }, [language]);

  const handleSummaryEdit = useCallback(async (targetStepId) => {
    const idx = normalizedSchema.findIndex(s => s.stepId === targetStepId);
    if (idx < 0 || !targetStepId) return;
    setLoading(true);
    setLoadingMessage(language === 'fr' ? 'Chargement...' : 'Loading...');
    setError('');
    try {
      const serverHistory = await readCanonicalHistory();
      const nextHistory = [...serverHistory];
      if (nextHistory[nextHistory.length - 1] !== targetStepId) nextHistory.push(targetStepId);
      await api.post('/api/intake-json', { history: nextHistory });
      setReturnToSummary(true);
      setStepData({});
      setVisibleHydration({});
      setStepIndex(idx);
    } catch (_) {
      setError(language === 'fr' ? 'Impossible de charger cette étape.' : 'Unable to load this step.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [language, normalizedSchema, readCanonicalHistory]);

  function renderComponent(component){
    let c = component;
    if(c.showIf && !evaluateCondition(c.showIf, mergedLogicData())) return null;
    if(c.hideIf && evaluateCondition(c.hideIf, mergedLogicData())) return null;
    c = inflateOptionChildren(c, __componentRefMap);
    const type=String(c.type||'').toLowerCase();
    const Comp=registry[type];
    if(!Comp) return null;
    const key=keyFor(c);
    const props={ comp:c, lang:language, values:stepData };
    // Portal conditional visibility for allowed types
    if(CONDITIONAL_VISIBILITY_TYPES.has(type) && key && hiddenConditionalKeys.has(key)) return null;
    if(type==='summary-list'){ props.values={ ...visibleHydration, ...stepData }; props.onEdit=handleSummaryEdit; }
    const needsValue = INPUT_TYPES.has(type);
    if(needsValue){ props.value=getVal(c); props.onChange=(val)=>handleFieldChange(c,val); props.error=errors[key]; }
    props.render = (child)=>renderComponent(child);
    return <Comp key={c.id || key} {...props} />;
  }

  function isConditionalChildActive(child){
    const containsTarget = (node) => {
      if (node === child) return true;
      if (!node || typeof node !== 'object') return false;
      if (Array.isArray(node.children) && node.children.some(containsTarget)) return true;
      if (Array.isArray(node.options)) {
        return node.options.some((opt) => optionRevealChildren(opt).some(containsTarget));
      }
      return false;
    };
    const visit = (components) => {
      for (const parent of components || []) {
        if (!parent || typeof parent !== 'object') continue;
        const type = String(parent.type || '').toLowerCase();
        if (Array.isArray(parent.options) && CONDITIONAL_OPTION_TYPES.has(type)) {
          const selected =
            type === 'checkboxes' || type === 'checkbox'
              ? (Array.isArray(getVal(parent)) ? getVal(parent).map(String) : [])
              : String(getVal(parent));
          for (const opt of parent.options) {
            const reveal = optionRevealChildren(opt);
            if (!reveal.some(containsTarget)) continue;
            const optionActive =
              type === 'checkboxes' || type === 'checkbox'
                ? selected.includes(String(opt.value))
                : String(opt.value) === selected;
            if (!optionActive) return false;
            return visit(reveal);
          }
        }
        if (Array.isArray(parent.children) && parent.children.some(containsTarget)) {
          return visit(parent.children);
        }
      }
      return true;
    };
    return visit(step?.components || []);
  }

  function computeValidation({ limitKeys = null, logDiagnostics = false, ruleTriggers = ['submit'] } = {}) {
    const stepErrors = {};
    const stepWarnings = {};
    const comps = flattenComponents();
    const data = mergedLogicData();
    const keyFilter = limitKeys ? new Set(limitKeys) : null;
    comps.forEach(c => {
      if (c.showIf && !evaluateCondition(c.showIf, data)) return;
      if (c.hideIf && evaluateCondition(c.hideIf, data)) return;
      const k = keyFor(c);
      if (!k) return;
      if (keyFilter && !keyFilter.has(k)) return;
      if (hiddenConditionalKeys.has(k)) return;
      if (!isConditionalChildActive(c)) return;
      const rawVal = getVal(c);
      const type = String(c.type || '').toLowerCase();
      const validationObj = migrateValidation(c.validation || c.props?.validation || {});
      const isRequired = c.required || validationObj.required;
      if (logDiagnostics && type === 'file-upload' && process.env.NODE_ENV !== 'production') {
        try {
          console.info('[intake] file-upload validation', {
            stepId: step?.stepId,
            key: k,
            required: !!isRequired,
            hidden: hiddenConditionalKeys.has(k),
            empty: valueIsEmpty(rawVal, type),
            value: rawVal,
            validation: validationObj
          });
        } catch (_) {}
      }
      if (isRequired && valueIsEmpty(rawVal, type)) {
        const reqMsg = validationObj.requiredMessage
          ? msgFor(validationObj.requiredMessage)
          : (validationObj.errorMessage
            ? msgFor(validationObj.errorMessage)
            : requiredMessage(type, language, validationObj && validationObj.errorMessage));
        stepErrors[k] = reqMsg || (language === 'fr' ? 'Ce champ est obligatoire' : 'This field is required');
        return;
      }
      if ((type === 'date' || type === 'date-input') && !valueIsEmpty(rawVal, type)) {
        const dateCheck = validateDateInputValue(rawVal, {
          disallowFuture: isDateOfBirthComponent(c, k),
        });
        if (!dateCheck.ok) {
          const configuredMessage =
            msgFor(validationObj.requiredMessage) ||
            msgFor(validationObj.errorMessage);
          stepErrors[k] = configuredMessage || (
            language === 'fr'
              ? 'Entrez une date valide.'
              : 'Enter a valid date.'
          );
          return;
        }
      }
      const effectiveType = type === 'input' ? String(c.inputType || '').toLowerCase() : type;
      if (!valueIsEmpty(rawVal, type)) {
        if (effectiveType === 'email') {
          const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(rawVal));
          if (!ok) {
            stepErrors[k] = emailMessage(language, validationObj && validationObj.errorMessage);
            return;
          }
        }
        if (effectiveType === 'number' || String(c.normalize || '').toLowerCase() === 'number') {
          const num = coerceNumber(rawVal);
          if (!Number.isFinite(num)) {
            stepErrors[k] = numberMessage(language, validationObj && validationObj.errorMessage);
            return;
          }
        }
      }
      for (const r of validationObj.rules) {
        const triggers = Array.isArray(r.trigger) ? r.trigger : ['submit'];
        if (!triggers.some(trigger => ruleTriggers.includes(trigger))) continue;
        const { failed, message } = evaluateRule(r, c, rawVal, data);
        if (failed) {
          if ((r.severity || 'error') === 'warn') {
            stepWarnings[k] = stepWarnings[k] || message;
            continue;
          } else {
            stepErrors[k] = message;
            if (r.block !== false) break;
          }
        }
      }
    });
    return { stepErrors, stepWarnings };
  }

  function validateStep(){
    focusErrorSummaryNext.current=true;
    const { stepErrors, stepWarnings } = computeValidation({ logDiagnostics: true, ruleTriggers: ['submit'] });
    setWarnings(stepWarnings);
    setErrors(stepErrors);
    return stepErrors;
  }

  async function handleNext(){
    setNavigationError('');
    setLoading(true);
    setError('');
    const finalStep = !hasVisibleNextStep;
    setLoadingMessage(finalStep
      ? (language==='fr'
        ? 'Envoi de votre demande… Nous générons vos formulaires signés. Cela peut prendre quelques secondes, merci de ne pas fermer cette page.'
        : 'Submitting your application… We’re generating your signed forms. This may take a few seconds; please don’t close this page.')
      : (language==='fr' ? 'Chargement...' : 'Loading...'));
    const stepErrs = validateStep();
    if (Object.keys(stepErrs).length) {
      setLoading(false);
      setLoadingMessage('');
      return;
    }
    const rawVals = stepData || {};
    const allComps = flattenComponents();
    const persistables = allComps.filter(isPersistable);
    let payload = {};
    let aggregatePatch = {};
    if (isAggregate) {
      const patch = {};
      persistables.forEach(c => {
        const k = keyFor(c);
        if (!k) return;
        const v = rawVals[k] ?? getVal(c, rawVals);
        const nv = normalizeValue(c, v);
        if (nv !== undefined) setByPath(patch, k, nv);
        if (patch[k] === undefined) patch[k] = null;
      });
      payload = patch;
      aggregatePatch = patch;
    } else {
      const flat = {};
      persistables.forEach(c => {
        const k = keyFor(c);
        if (!k) return;
        const v = rawVals[k] ?? getVal(c, rawVals);
        const nv = normalizeValue(c, v);
        if (nv !== undefined) flat[k] = nv;
        if (flat[k] === undefined) flat[k] = null;
      });
      payload = flat;
      const patch = {};
      Object.entries(flat).forEach(([k, v]) => setByPath(patch, k, v));
      aggregatePatch = patch;
    }
    let movedToNextStep = false;
    try {
      await api.post(saveUrl, payload);
      try { await api.post('/api/intake-json', aggregatePatch); } catch (_) {}
      const navigationEnvelope = await fetchStepEnvelope(step?.stepId);
      const navigationIds = navigationEnvelope.applicableStepIds;
      setApplicableStepIds(navigationIds);
      const currentNavigationIndex = navigationIds.indexOf(step?.stepId);
      // Absence from the current path is not proof that this is its final step.
      if (currentNavigationIndex < 0) {
        throw new Error('workflow_navigation_changed');
      }
      let nextStepId = navigationIds[currentNavigationIndex + 1] || null;
      let nextIdx = nextStepId
        ? normalizedSchema.findIndex(candidate => candidate.stepId === nextStepId)
        : -1;
      if (returnToSummary) {
        const summaryIdx = normalizedSchema.findIndex(s => s.stepId === 'iset-summary');
        if (summaryIdx !== -1) {
          nextIdx = summaryIdx;
          nextStepId = 'iset-summary';
        }
        setReturnToSummary(false);
      }
      if (nextStepId && nextIdx < 0) {
        // An already-open tab may predate publication of the next step. Save
        // first, then refresh the form definition before interpreting navigation.
        const refreshed = await refreshSchema();
        const refreshedSteps = refreshed?.data?.schema;
        nextIdx = Array.isArray(refreshedSteps)
          ? refreshedSteps.findIndex(candidate => candidate.stepId === nextStepId)
          : -1;
        if (nextIdx < 0) throw new Error('workflow_navigation_changed');
      }
      if (nextIdx !== -1 && nextStepId) {
        try {
          const serverHistory = navigationEnvelope.history.length
            ? navigationEnvelope.history
            : await readCanonicalHistory();
          const nextHistory = [...serverHistory];
          if (step?.stepId && nextHistory[nextHistory.length - 1] !== step.stepId) nextHistory.push(step.stepId);
          if (nextHistory[nextHistory.length - 1] !== nextStepId) nextHistory.push(nextStepId);
          await api.post('/api/intake-json', { history: nextHistory });
        } catch (_) {}
        await persistRecoveryDraft({ stepCursor: nextStepId, draftPatch: aggregatePatch });
        setStepData({});
        setVisibleHydration({});
        movedToNextStep = true;
        setStepIndex(nextIdx);
        return;
      }
      try {
        const resp = await api.post('/api/intake/complete');
        const ref = resp?.data?.reference_number;
        const ts = resp?.data?.submitted_at;
        const qs = new URLSearchParams();
        if (ref) qs.set('ref', ref);
        if (ts) qs.set('ts', ts);
        navigate(`/submission/confirmation${qs.toString() ? `?${qs.toString()}` : ''}`);
      } catch (submitErr) {
        const errorData = submitErr?.response?.data || {};
        const serverMessage = language === 'fr'
          ? (errorData.message_fr || errorData.message)
          : errorData.message;
        setError(serverMessage || (language==='fr' ? 'Impossible de soumettre la demande.' : 'Unable to submit application.'));
      }
    } catch (saveErr) {
      if (saveErr?.message === 'workflow_navigation_changed') {
        setNavigationError(language === 'fr'
          ? 'Le formulaire a changé. Vos réponses sont enregistrées. Réessayez pour continuer.'
          : 'The form has changed. Your answers are saved. Try again to continue.');
      } else {
        setError(language === 'fr' ? 'Impossible d’enregistrer cette étape.' : 'Unable to save step data.');
      }
    } finally {
      // The next step's hydration effect owns its loading state. Releasing it
      // here can expose controls before their values and validation are ready.
      if (!movedToNextStep) setLoading(false);
      setLoadingMessage('');
    }
  }

  async function handleBack(){
    setNavigationError('');
    setError('');
    setErrors({});
    setWarnings({});
    focusErrorSummaryNext.current=false;
    setLiveAnnouncement('');
    setLoading(true);
    setLoadingMessage(language==='fr' ? 'Chargement...' : 'Loading...');
    try {
      let draftSnapshotForAutosave = {};
      const compsAll = flattenComponents();
      const persistables = compsAll.filter(isPersistable);
      if (persistables.length) {
        const rawVals = stepData || {};
        const flat = {};
        persistables.forEach(c => {
          const k = keyFor(c);
          if (!k) return;
          const v = rawVals[k] ?? getVal(c, rawVals);
          const nv = normalizeValue(c, v);
          if (nv !== undefined) flat[k] = nv;
          if (flat[k] === undefined) flat[k] = null;
        });
        const payload = isAggregate
          ? (() => {
            const p = {};
            Object.entries(flat).forEach(([k, v]) => setByPath(p, k, v));
            return p;
          })()
          : flat;
        let aggregateSnapshot = payload;
        try {
          await api.post(saveUrl, payload);
          aggregateSnapshot = isAggregate
            ? payload
            : (() => {
              const p = {};
              Object.entries(flat).forEach(([k, v]) => setByPath(p, k, v));
              return p;
            })();
          try { await api.post('/api/intake-json', aggregateSnapshot); } catch (_) {}
        } catch (_) {}
        draftSnapshotForAutosave = aggregateSnapshot;
      }
      const navigationEnvelope = await fetchStepEnvelope(step?.stepId);
      const navigationIds = navigationEnvelope.applicableStepIds;
      const applicableSet = new Set(navigationIds);
      setApplicableStepIds(navigationIds);
      let historyStack = navigationEnvelope.history;
      try {
        if (!historyStack.length) historyStack = await readCanonicalHistory();
      } catch (_) {}
      if (historyStack.length) {
        if (step?.stepId && historyStack[historyStack.length - 1] === step.stepId) historyStack.pop();
        else historyStack.pop();
        while (historyStack.length && !applicableSet.has(historyStack[historyStack.length - 1])) historyStack.pop();
        try { await api.post('/api/intake-json', { history: historyStack }); } catch (_) {}
      }
      const prevStepId = historyStack[historyStack.length - 1];
      let prevIdx = prevStepId ? normalizedSchema.findIndex(s => s.stepId === prevStepId) : -1;
      if (prevIdx < 0) {
        const currentNavigationIndex = navigationIds.indexOf(step?.stepId);
        const fallbackStepId = currentNavigationIndex > 0 ? navigationIds[currentNavigationIndex - 1] : null;
        prevIdx = normalizedSchema.findIndex(candidate => candidate.stepId === fallbackStepId);
      }
      const targetStepId = prevIdx >= 0 ? normalizedSchema[prevIdx]?.stepId : normalizedSchema[0]?.stepId;
      await persistRecoveryDraft({ stepCursor: targetStepId, draftPatch: draftSnapshotForAutosave });
      setStepData({});
      setVisibleHydration({});
      setStepIndex(prevIdx >= 0 ? prevIdx : 0);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }

  async function handleSaveDraft(){
    if (!allowSaveDraft) return;
    try {
      const comps=flattenComponents().filter(isPersistable);
      const patch={};
      comps.forEach(c=>{
        const k=keyFor(c);
        if(!k) return;
        const v=getVal(c);
        patch[k]= v === undefined ? null : v;
        if(patch[k]===undefined) patch[k]=null;
      });
      const serverHistory = await readCanonicalHistory();
      const currentStepId = step?.stepId || 'dynamic-test';
      const allowHistoryAppend = serverHistory[serverHistory.length - 1] !== currentStepId;
      await api.post('/api/draft',{
        stepCursor: currentStepId,
        dataPatch: patch,
        historyAppend: allowHistoryAppend,
        saveForLater: true
      });
      navigate('/dashboard?draftSaved=1');
    } catch(e){
      console.error('[dynamic] save draft failed', e);
      alert(language==='fr'? 'Échec de la sauvegarde du brouillon.' : 'Failed to save draft.');
    }
  }

  if(!step) return <div>No steps defined in schema.</div>;

  return (
    <div className="govuk-width-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
      {currentStepNumber > 1 && (
        <a href={stepIndex > 1 ? '#previous-step' : '#first-step'} className="govuk-back-link" onClick={(e)=>{ e.preventDefault(); if(!loading) handleBack(); }} aria-label={language==='fr'? 'Retour à l\'étape précédente' : 'Back to previous step'}>
          {language==='fr'? 'Retour' : 'Back'}
        </a>
      )}
      <div className="govuk-!-margin-bottom-3" aria-live="polite">
        <p className="govuk-body govuk-!-margin-bottom-1">
          {language==='fr' ? `Étape ${currentStepNumber} sur ${totalSteps}` : `Step ${currentStepNumber} of ${totalSteps}`}
        </p>
        <div className="govuk-!-margin-bottom-2" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progressPercent} aria-label={language==='fr' ? 'Progression du formulaire' : 'Form progress'} style={{ height: 8, background: 'var(--app-progress-track)', borderRadius: 4 }}>
          <div style={{ width: `${progressPercent}%`, height: '100%', background: 'var(--app-progress-fill)', borderRadius: 4 }} />
        </div>
      </div>
      {Object.keys(errors||{}).length>0 && (()=>{ const errored=flattenComponents().filter(c=>errors[keyFor(c)]); const count=errored.length; const enLine=count===1? 'The following issues are preventing you from progressing to the next step.' : `Fix the ${count} highlighted fields below.`; const frLine=count===1? 'Les problèmes suivants vous empêchent de passer à l’étape suivante.' : `Corrigez les ${count} champs en surbrillance ci-dessous.`; return (<div ref={errorSummaryRef} tabIndex="-1" className="govuk-error-summary" aria-labelledby="error-summary-title" aria-describedby="error-summary-desc" role="alert" data-module="govuk-error-summary"><h2 className="govuk-error-summary__title" id="error-summary-title">{language==='fr'? 'Un problème est survenu':'There is a problem'}</h2><p id="error-summary-desc" className="govuk-body govuk-!-margin-bottom-2" style={{marginTop:0}}>{language==='fr'? frLine: enLine}</p><div className="govuk-error-summary__body"><ul className="govuk-list govuk-error-summary__list">{errored.map(c=>{ const k=keyFor(c); const anchor=anchorIdFor(c); return (<li key={k}><a href={`#${anchor}`}>{errors[k]}</a></li>); })}</ul></div></div>); })()}
  {/* Preview modal removed; submission now happens directly on final step */}
      {step.description && <p>{typeof step.description==='object'? step.description[language] : step.description}</p>}
      {(navigationError || error) && <div className="govuk-error-message" role="alert">{navigationError || error}</div>}
      {loading ? (
        <div className="govuk-inset-text" role="status" aria-live="polite">
          <p className="govuk-body govuk-!-margin-bottom-1" style={{ fontWeight: 600 }}>
            {loadingMessage || (language==='fr' ? 'Chargement...' : 'Loading...')}
          </p>
          {!hasVisibleNextStep && (
            <p className="govuk-body govuk-!-margin-bottom-0">
              {language==='fr'
                ? 'Nous finalisons votre demande et générons vos formulaires signés. Cela peut prendre jusqu’à 15 secondes.'
                : 'We’re finalizing your submission and generating your signed forms. This can take up to ~15 seconds.'}
            </p>
          )}
        </div>
      ) : (
        <form noValidate ref={formRef} onSubmit={e=>{ e.preventDefault(); handleNext(); }}>
          <div aria-live="polite" className="govuk-visually-hidden">{liveAnnouncement}</div>
          {step.components?.map(renderComponent)}
          {allUploadsHidden && (
            <div className="govuk-inset-text" style={{ marginTop: 16 }}>
              {language==='fr' ? 'Aucun document n\'est requis pour cette étape en fonction de vos réponses actuelles.' : 'No documents are required for this step based on your current answers.'}
            </div>
          )}
          <div className="govuk-button-group" style={{ marginTop:32 }}>
            <button type="submit" className="govuk-button" disabled={loading}>{hasVisibleNextStep ? (language==='fr'? 'Suivant':'Next') : (language==='fr'? 'Soumettre':'Submit')}</button>
            {allowSaveDraft && (
              <button type="button" className="govuk-button govuk-button--secondary" onClick={handleSaveDraft} disabled={loading}>{language==='fr'? 'Enregistrer et terminer plus tard':'Save and finish later'}</button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function DynamicTest() {
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const navigate = useNavigate();
  const { status: schemaStatus, schema, runtime, error: schemaError, refresh: refreshSchema } = useWorkflowSchema();
  const [startEligibility, setStartEligibility] = useState({
    loading: true,
    error: null,
    canStart: false,
  });

  useEffect(() => {
    let cancelled = false;
    setStartEligibility({ loading: true, error: null, canStart: false });
    api.get('/api/application-start-eligibility')
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || {};
        const explicitlyAllowed =
          data.canStartNewApplication === true || data.can_start_new_application === true;
        const explicitlyBlocked =
          data.canStartNewApplication === false || data.can_start_new_application === false;
        if (explicitlyBlocked) {
          navigate('/dashboard?applicationInProgress=1', { replace: true });
          setStartEligibility({ loading: false, error: null, canStart: false });
          return;
        }
        if (!explicitlyAllowed) {
          setStartEligibility({
            loading: false,
            error: 'application_start_eligibility_unknown',
            canStart: false,
          });
          return;
        }
        setStartEligibility({ loading: false, error: null, canStart: true });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 409) {
          navigate('/dashboard?applicationInProgress=1', { replace: true });
          setStartEligibility({ loading: false, error: null, canStart: false });
          return;
        }
        console.error('[DynamicTest] application start eligibility check failed', err);
        setStartEligibility({
          loading: false,
          error: 'application_start_eligibility_unavailable',
          canStart: false,
        });
      });
    return () => { cancelled = true; };
  }, [navigate]);

  if (startEligibility.loading || schemaStatus === WORKFLOW_SCHEMA_STATUS.LOADING || schemaStatus === WORKFLOW_SCHEMA_STATUS.IDLE) {
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <p className="govuk-body">
          {language === 'fr' ? 'Chargement du formulaire de demande...' : 'Loading the application form...'}
        </p>
      </div>
    );
  }

  if (startEligibility.error || !startEligibility.canStart) {
    const message = language === 'fr'
      ? 'Nous ne pouvons pas confirmer si vous pouvez commencer une nouvelle demande pour le moment. Retournez à votre tableau de bord et réessayez.'
      : 'We cannot confirm whether you can start a new application right now. Return to your dashboard and try again.';
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <div className="govuk-warning-text">
          <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
          <strong className="govuk-warning-text__text">{message}</strong>
        </div>
        <button type="button" className="govuk-button" onClick={() => navigate('/dashboard')}>
          {language === 'fr' ? 'Retour au tableau de bord' : 'Return to dashboard'}
        </button>
      </div>
    );
  }

  if (schemaStatus === WORKFLOW_SCHEMA_STATUS.ERROR) {
    const applicantAccountRequired = schemaError === 'applicant_account_required';
    const message = applicantAccountRequired
      ? (language === 'fr'
        ? 'Ce formulaire est réservé aux comptes du portail des demandeurs. Déconnectez-vous, puis reconnectez-vous avec un compte demandeur.'
        : 'This form is for applicant portal accounts. Please sign out, then sign in with an applicant account.')
      : (language === 'fr'
        ? 'Nous ne pouvons pas charger le formulaire pour le moment. Réessayez plus tard ou contactez le soutien.'
        : 'We cannot load the application form right now. Please try again later or contact support.');
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <div className="govuk-warning-text">
          <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
          <strong className="govuk-warning-text__text">
            {message}
            {schemaError && !applicantAccountRequired ? ` (${schemaError})` : ''}
          </strong>
        </div>
        {!applicantAccountRequired && (
          <button type="button" className="govuk-button" onClick={() => refreshSchema()}>
            {language === 'fr' ? 'Réessayer' : 'Try again'}
          </button>
        )}
      </div>
    );
  }

  const intakeFormSchema = Array.isArray(schema) ? schema : [];
  const workflowType =
    runtime?.meta?.schemaMeta?.workflow?.type ||
    runtime?.meta?.schemaMeta?.workflow?.workflow_type ||
    runtime?.schemaEnvelope?.meta?.workflow?.type ||
    runtime?.schemaEnvelope?.meta?.workflow?.workflow_type ||
    null;
  const allowSaveDraft = workflowType === 'main-intake';

  if (!intakeFormSchema.length) {
    const message = language === 'fr'
      ? 'Aucun parcours de demande n’est actuellement publié.'
      : 'No intake workflow is currently published.';
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
        <div className="govuk-warning-text">
          <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
          <strong className="govuk-warning-text__text">{message}</strong>
        </div>
      </div>
    );
  }

  return <DynamicTestInner intakeFormSchema={intakeFormSchema} allowSaveDraft={allowSaveDraft} refreshSchema={refreshSchema} />;
}

export default DynamicTest;
